import { Link, NavLink } from "react-router-dom";
import { useIsFetching, useQueryClient } from "react-query";
import { LayoutDashboard, ListOrdered, RefreshCw, Target, TrendingUp } from "lucide-react";
import { cn } from "../../utilities/helpers";
import { CurrencySelectorDropdownNew } from "../dropdown";
import AddInvestment from "../AddInvestments";

const NAV_ITEMS = [
	{ to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
	{ to: "/transactions", label: "Transactions", icon: ListOrdered },
	{ to: "/goals", label: "Goals", icon: Target },
];

const REFRESH_KEYS = [
	"fetchInvestments",
	"fetchAssets",
	"fetchAllAssetLatestPrice",
	"fetchDaily",
	"fetchDividends",
	"fetchInvestmentSummary",
	"fetchCurrencies",
	"fetchGoals",
];

const navLinkClass = ({ isActive }) =>
	cn(
		"inline-flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-2 text-sm font-medium transition-colors",
		isActive
			? "bg-slate-900 text-white"
			: "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
	);

const AppShell = ({ title, description, actions, children }) => {
	const queryClient = useQueryClient();
	const isFetching = useIsFetching() > 0;

	const handleRefresh = () => {
		REFRESH_KEYS.forEach((key) => queryClient.invalidateQueries(key));
	};

	return (
		<div className="min-h-screen bg-slate-50">
			<a
				href="#main-content"
				className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-3 focus:z-50 focus:rounded-md focus:bg-slate-900 focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-white"
			>
				Skip to content
			</a>
			<header className="sticky top-0 z-40 border-b border-slate-200 bg-white/90 backdrop-blur">
				<div className="mx-auto flex h-14 max-w-7xl items-center gap-4 px-4 md:px-6">
					<Link to="/" className="flex items-center gap-2 font-semibold text-slate-900">
						<span className="flex h-7 w-7 items-center justify-center rounded-md bg-slate-900 text-white">
							<TrendingUp className="h-4 w-4" aria-hidden="true" />
						</span>
						<span className="hidden sm:inline">Portfolio</span>
					</Link>
					<nav aria-label="Main" className="hidden items-center gap-1 md:flex">
						{NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
							<NavLink key={to} to={to} end={end} className={navLinkClass}>
								<Icon className="h-4 w-4" aria-hidden="true" />
								{label}
							</NavLink>
						))}
					</nav>
					<div className="ml-auto flex items-center gap-2">
						<CurrencySelectorDropdownNew />
						<button
							type="button"
							onClick={handleRefresh}
							className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
							aria-label="Refresh data"
							title="Refresh data"
						>
							<RefreshCw className={cn("h-4 w-4", isFetching && "animate-spin")} aria-hidden="true" />
						</button>
						<AddInvestment />
					</div>
				</div>
				<nav
					aria-label="Main (compact)"
					className="mx-auto flex max-w-7xl gap-1 overflow-x-auto border-t border-slate-100 px-4 py-2 md:hidden"
				>
					{NAV_ITEMS.map(({ to, label, icon: Icon, end }) => (
						<NavLink key={to} to={to} end={end} className={navLinkClass}>
							<Icon className="h-4 w-4" aria-hidden="true" />
							{label}
						</NavLink>
					))}
				</nav>
			</header>

			<main
				id="main-content"
				tabIndex={-1}
				className="mx-auto max-w-7xl space-y-6 px-4 py-6 outline-none md:px-6 lg:py-8"
			>
				{(title || actions) && (
					<div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
						<div>
							{title && (
								<h1 className="text-2xl font-semibold tracking-tight text-slate-900">
									{title}
								</h1>
							)}
							{description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
						</div>
						{actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
					</div>
				)}
				{children}
			</main>
		</div>
	);
};

export default AppShell;
